import { useActiveCompanyId } from '@/lib/active-company';
import {
  useResponseSummary,
  useStandardReport,
  type ModuleResponseSummary,
} from '@/lib/assessments';
import { useSnapshots } from '@/lib/snapshots';
import { StatTile } from '@/components/StatTile';
import { ReportHeader } from './_shared/ReportHeader';
import { ModuleRollup } from './_shared/ModuleRollup';
import { Deficiencies } from './_shared/Deficiencies';
import { AgencyStrip } from './_shared/AgencyStrip';
import { TrendCard } from './_shared/TrendCard';
import { catalogStamp } from '@/lib/catalog-stamp';

export function LiveView() {
  const companyId = useActiveCompanyId();
  const summary = useResponseSummary(companyId);
  const standardReport = useStandardReport(companyId);
  const snapshots = useSnapshots(companyId);

  if (!companyId) return <Empty title="Select a company first." />;
  if (summary.isLoading) return <Empty title="Loading…" />;
  if (summary.isError) return <Empty title="Couldn't load report." retry={() => summary.refetch()} />;
  const data = summary.data;
  if (!data) return <Empty title="No assessment in progress." />;

  const totals = aggregate(data.modules);

  return (
    <div className="mx-auto flex max-w-content-wide flex-col gap-gap-group px-5 py-gap-group md:px-10 lg:px-20">
      <ReportHeader
        mode="live"
        updatedAt={data.updatedAt}
        stamp={catalogStamp({
          catalogReleaseVersion: data.catalogReleaseVersion ?? null,
          asOf: data.asOf ?? null,
        })}
      />

      <section className="grid gap-gap-element-lg md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Compliance"
          value={`${totals.compliancePct}%`}
          subtext={`${totals.compliant}/${totals.total}`}
          accent="accent"
          progressPct={totals.compliancePct}
        />
        <StatTile
          label="Items assessed"
          value={String(totals.assessed)}
          subtext={`of ${totals.total}`}
          accent="accent"
          progressPct={totals.total > 0 ? Math.round((totals.assessed / totals.total) * 100) : 0}
        />
        <StatTile
          label="Open warnings"
          value={String(totals.warnings)}
          accent={totals.warnings > 0 ? 'danger' : 'accent'}
          progressPct={totals.total > 0 ? Math.round((totals.warnings / totals.total) * 100) : 0}
        />
        <StatTile
          label="Modules in progress"
          value={String(totals.modulesInProgress)}
          subtext={`of ${data.modules.length}`}
          accent="accent"
          progressPct={
            data.modules.length > 0
              ? Math.round((totals.modulesInProgress / data.modules.length) * 100)
              : 0
          }
        />
      </section>

      {snapshots.data ? <TrendCard snapshots={snapshots.data} /> : null}

      <ModuleRollup modules={data.modules} linked />

      <Deficiencies modules={data.modules} linked />

      {standardReport.data && standardReport.data.agencies.length > 0 ? (
        <AgencyStrip agencies={standardReport.data.agencies} />
      ) : null}
    </div>
  );
}

export function aggregate(modules: ModuleResponseSummary[]) {
  let total = 0;
  let compliant = 0;
  let warnings = 0;
  let modulesInProgress = 0;
  for (const m of modules) {
    const r = m.summary.itemResponses;
    total += r.total;
    compliant += r.meetsStandardCount;
    for (const w of r.warnings) warnings += w.count;
    if (r.meetsStandardCount > 0 && r.meetsStandardCount < r.total) modulesInProgress += 1;
  }
  const assessed = compliant + warnings;
  const compliancePct = total > 0 ? Math.round((compliant / total) * 100) : 0;
  return { total, compliant, assessed, warnings, modulesInProgress, compliancePct };
}

function Empty({ title, retry }: { title: string; retry?: () => void }) {
  return (
    <div className="mx-auto max-w-content-wide px-5 py-gap-group md:px-10 lg:px-20">
      <p className="text-body text-text-secondary">{title}</p>
      {retry ? (
        <button onClick={retry} className="mt-3 text-body-sm text-accent">
          Retry
        </button>
      ) : null}
    </div>
  );
}
